import { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from "react"
import { Box, Button, CircularProgress, Tooltip } from "@mui/material"
import { IndicatorBoxContainer, fabStyle } from "components/common-styles"

import { DataGrid, GridToolbarColumnsButton, GridToolbarContainer } from "@mui/x-data-grid"
import SearchRequiredWarningImage from "assets/estore-FirstPage.png"
import NoRecordImage from "assets/estore-NoRecord.png"
import * as StorageHelper from "utils/storage-helper"
import { WarningMessageWrapper, WarningMessage, WarningMessageContainer } from "./styles"

import AddIcon from "@mui/icons-material/Add"
import FileUploadIcon from "@mui/icons-material/FileUpload"
import CustomExportButton from "./custom-export-button"
import ResetDefaultButton from "./reset-default-button"
import { ManageHistory } from "@mui/icons-material"
import UploadHistoryDialog from "./upload/upload-history-dialog"
import BulkDeleteButton from "./bulk-delete-button"
import ConfirmDialog from "./confirm-dialog"
import AlertDialog from "./alert-dialog"
import { entityFromExportName, trackEvent } from "utils/analytics"

function CustomToolbar(props) {
  return (
    <GridToolbarContainer
      sx={{
        display: "flex",
        justifyContent: "space-between", // space between left and right
        alignItems: "center",
        padding: 1,
      }}
    >
      {/* Left side: Default toolbar items */}
      <Box sx={{ display: "flex", gap: 1 }}>
        <Tooltip placement="top" title="Show/hide columns">
          <GridToolbarColumnsButton />
        </Tooltip>

        <CustomExportButton exportOptions={props.exportOptions} />
        <ResetDefaultButton options={props.resetOptions} />
      </Box>

      {/* Right side: Custom button */}
      {props.bulkDeleteOptions && (
        <Box>
          <BulkDeleteButton options={props.bulkDeleteOptions} />
        </Box>
      )}
    </GridToolbarContainer>
  )
}

export const CommonListResult = forwardRef(
  (
    {
      isLoading,
      data,
      company,
      userConfig,
      searchText,
      gridStorageName,
      searchRequiredWarningMsg,
      noDataMsg,
      columns,
      hiddenColumns,
      exportFileName,
      moduleName,
      idProp,
      columnsExtra,
      exportColumnsExtra,
      handleAddClicked,
      handleUploadClicked,
      handleRowClicked,
      handleBulkDelete,
    },
    ref,
  ) => {
    const [currentHiddenColumns, setCurrentHiddenColumns] = useState(hiddenColumns)
    const [currentColumns, setCurrentColumns] = useState(columns)
    const [exportingColumns, setExportingColumns] = useState([...columns, ...(exportColumnsExtra || [])])
    const [showHistory, setShowHistory] = useState(false)
    const [openDelete, setOpenDelete] = useState(false)
    const [openAlert, setOpenAlert] = useState(false)
    const [alertContent, setAlertContent] = useState("")
    const [selectionModel, setSelectionModel] = useState([])

    const saveGridData = useCallback(
      data => {
        StorageHelper.setStorage(gridStorageName, JSON.stringify(data))
      },
      [gridStorageName, data],
    )

    useEffect(() => {
      if (userConfig?.Config_Value?.HiddenColumns || userConfig?.Config_Value?.Columns) {
        if (userConfig.Config_Value.HiddenColumns) setCurrentHiddenColumns(userConfig.Config_Value.HiddenColumns)

        if (userConfig.Config_Value.Columns) setCurrentColumns(userConfig.Config_Value.Columns)
        return
      }

      const savedData = StorageHelper.getStorage(gridStorageName)
      if (savedData) {
        const parsed = JSON.parse(savedData)
        if (Array.isArray(parsed)) saveGridData(hiddenColumns)
        else setCurrentHiddenColumns(parsed)
      } else {
        setCurrentHiddenColumns(hiddenColumns)
      }
    }, [gridStorageName, hiddenColumns, userConfig])

    useEffect(() => {
      setCurrentColumns([...columns, ...(columnsExtra || [])])
      // exportColumnsExtra: columns that go in the export but not on the grid.
      setExportingColumns([...columns, ...(columnsExtra || []), ...(exportColumnsExtra || [])])
    }, [columnsExtra, exportColumnsExtra])

    const handleColumnVisibilityChanged = newModel => {
      setCurrentHiddenColumns(newModel)
      saveGridData(newModel)
    }

    const handleColumnWidthChanged = params => {
      const newColumn = params.colDef
      const newColumns = currentColumns.map(x => {
        if (x.field == newColumn.field) return newColumn
        return x
      })
      setCurrentColumns(newColumns)
    }

    const handleResetGrid = () => {
      setCurrentColumns(columns)
      setCurrentHiddenColumns(hiddenColumns)
    }

    useImperativeHandle(ref, () => ({
      getCurrentHiddenColumns() {
        return currentHiddenColumns
      },
      getCurrentColumns() {
        return currentColumns
      },
    }))

    const handleUploadHistoryClicked = () => {
      setShowHistory(true)
    }

    const onConfirmDelete = async () => {
      const res = await handleBulkDelete(selectionModel)
      const failed = res?.length || 0
      trackEvent("record_delete", {
        entity: entityFromExportName(exportFileName),
        count: selectionModel.length,
        bulk: "true",
        result: failed === 0 ? "success" : failed < selectionModel.length ? "partial" : "failed",
      })
      setSelectionModel([])
      if (res?.length > 0) {
        let msg = "The following orders could not be deleted:<br/>"
        res.forEach(x => {
          msg += `- ${x.OrderNumber} (${x.StatusMessage})<br/>`
        })
        setAlertContent(msg)
        setOpenAlert(true)
      }
    }

    return (
      <WarningMessageContainer id="searchResultWrapper">
        {isLoading ? (
          <IndicatorBoxContainer>
            <CircularProgress size={64} />
          </IndicatorBoxContainer>
        ) : !searchText || (searchText && data.length == 0) ? (
          <WarningMessageWrapper>
            <Box component="img" sx={{ maxWidth: 200 }} src={!searchText ? SearchRequiredWarningImage : NoRecordImage} />
            <WarningMessage>{!searchText ? searchRequiredWarningMsg : noDataMsg}</WarningMessage>
          </WarningMessageWrapper>
        ) : (
          <DataGrid
            getRowId={row => row[`${idProp ? idProp : "ID"}`]}
            rows={data}
            columns={currentColumns}
            columnVisibilityModel={currentHiddenColumns}
            checkboxSelection={Boolean(handleBulkDelete)}
            rowSelectionModel={selectionModel}
            onRowSelectionModelChange={newSelection => {
              setSelectionModel(newSelection)
            }}
            onColumnVisibilityModelChange={handleColumnVisibilityChanged}
            onColumnWidthChange={handleColumnWidthChanged}
            disableRowSelectionOnClick
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 50,
                },
              },
            }}
            pageSizeOptions={[20, 30, 50, 100]}
            slots={{
              toolbar: CustomToolbar,
            }}
            slotProps={{
              toolbar: {
                exportOptions: { hiddenColumns: currentHiddenColumns, columns: exportingColumns, fileName: exportFileName, data },
                resetOptions: { handleResetGrid: handleResetGrid },
                bulkDeleteOptions: handleBulkDelete ? { selectedItems: selectionModel, handleBulkDelete: () => setOpenDelete(true) } : null,
              },
            }}
            onRowDoubleClick={handleRowClicked}
            getRowClassName={params => (params.indexRelativeToCurrentPage % 2 === 0 ? "Mui-even" : "Mui-odd")}
            sx={{
              "& .Mui-odd": {
                backgroundColor: "#f5f5f5",
              },
            }}
          />
        )}

        {company && handleAddClicked && (
          <>
            <Button id="btnAdd" sx={[fabStyle, { left: 8 }]} color="grey" variant="contained" startIcon={<AddIcon />} onClick={handleAddClicked}>
              Add new
            </Button>

            {handleUploadClicked && (
              <>
                <Button id="btnUpload" sx={[fabStyle, { left: 140 }]} color="grey" variant="contained" startIcon={<FileUploadIcon />} onClick={handleUploadClicked}>
                  Upload
                </Button>

                {showHistory && <UploadHistoryDialog moduleName={moduleName} open={true} onClose={() => setShowHistory(false)} />}
              </>
            )}
          </>
        )}

        {handleBulkDelete && (
          <>
            <ConfirmDialog open={openDelete} onClose={() => setOpenDelete(false)} onConfirm={onConfirmDelete} />
            <AlertDialog open={openAlert} content={alertContent} onClose={() => setOpenAlert(false)} />
          </>
        )}
      </WarningMessageContainer>
    )
  },
)
